// core/react/auth.jsx — who's signed in and how they got there: sign in,
// start a trial, sign up and pay on Stripe, save the passcode, renew.
// Ported from SyncedCue's auth.jsx and shared by both apps. The rules
// (plans, passcodes, Stripe links) come from each app's src/account.mjs.
//
// Honest limit: every check in a browser-only app can be bypassed by someone
// editing the page in devtools. Passcodes are signed, so they can't be
// invented or edited without the salt, but this gate keeps honest people
// honest, no more.
import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { Sheet, toast, Icon } from './ui'
import { vibrate } from '../util'
import { celebrate } from './celebrate'

/* ═══ Admin sign-in: only a salted PBKDF2 hash ships (npm run admin-hash) ═══ */
const hexToBytes = (hex) => new Uint8Array(hex.match(/.{2}/g).map((h) => parseInt(h, 16)))
const toHex = (buf) => Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('')
async function pbkdf2Hex(text, saltHex, iterations) {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(text), 'PBKDF2', false, ['deriveBits'])
  return toHex(await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: hexToBytes(saltHex), iterations }, key, 256))
}
const sameHex = (a, b) => { if (a.length !== b.length) return false; let d = 0; for (let i = 0; i < a.length; i++) d |= a.charCodeAt(i) ^ b.charCodeAt(i); return d === 0 }
async function isAdmin(admin, name, code) {
  if (!admin || !admin.hash) return false
  const norm = name.trim().replace(/\s+/g, ' ').toLowerCase()
  return sameHex(await pbkdf2Hex(`${norm}:${code.trim()}`, admin.salt, admin.iterations), admin.hash)
}

/** "  jude   mensah " → "Jude Mensah" — how names are displayed. */
export const displayName = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase().replace(/(^|[\s'-])(\p{L})/gu, (_, sep, c) => sep + c.toUpperCase())

// Slows down guessing: after 5 wrong tries, wait 30 seconds (per tab).
const failures = { count: 0, until: 0 }
function checkThrottle() { if (Date.now() < failures.until) throw new Error(`Too many tries. Wait ${Math.ceil((failures.until - Date.now()) / 1000)} seconds and try again.`) }
function noteFailure() { failures.count += 1; if (failures.count >= 5) { failures.until = Date.now() + 30000; failures.count = 0 } }

/**
 * While guardRef.current is true: closing or reloading the tab asks "Leave site?", and the
 * Back button stays on this page with a reminder. Browsers only show that prompt after the
 * person has tapped or typed on the page, and phones may skip it, so the passcode is also
 * remembered (see passcodeUnsaved and the reminder in AuthProvider).
 */
export function useLeaveGuard(guardRef, message = 'Save your passcode first. There’s no way to get it back later.') {
  useEffect(() => {
    const onBefore = (e) => { if (!guardRef.current) return undefined; e.preventDefault(); e.returnValue = ''; return '' }
    const onPop = () => {
      if (!guardRef.current) return
      try { window.history.pushState({ leaveGuard: 1 }, '', window.location.href) } catch { /* ignore */ }
      toast(message)
    }
    window.addEventListener('beforeunload', onBefore)
    window.addEventListener('popstate', onPop)
    try { if (guardRef.current && !window.history.state?.leaveGuard) window.history.pushState({ leaveGuard: 1 }, '', window.location.href) } catch { /* ignore */ }
    return () => { window.removeEventListener('beforeunload', onBefore); window.removeEventListener('popstate', onPop) }
  }, [guardRef, message])
}
let revealsOpen = 0   // passcode screens on the page right now: the reminder waits while one is up

const AuthCtx = createContext(null)
export const useAuth = () => useContext(AuthCtx)

/**
 * @param account  the object from createAccount() (src/account.mjs)
 * @param admin    { salt, hash, iterations } or null
 * @param paths    { start: '/start', checkout: '/checkout' }
 * @param gateCopy { [feature]: 'Sentence shown when someone hits that gate' }
 */
export function AuthProvider({ account, admin, paths = { start: '/start', checkout: '/checkout' }, gateCopy = {}, upgradeTitle = null, children }) {
  const A = account
  const SESSION_KEY = `${A.prefix}_auth_v1`
  const remembered = () => A.readSaved(SESSION_KEY) || A.readSaved(SESSION_KEY, { session: true })
  const forget = useCallback(() => { A.writeSaved(SESSION_KEY, null); A.writeSaved(SESSION_KEY, null, { session: true }) }, [A, SESSION_KEY])
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sheet, setSheet] = useState(null)   // null | { feature }

  useEffect(() => {
    (async () => {
      const saved = remembered()
      try {
        if (saved?.admin) setSession({ name: saved.name, admin: true, plan: A.PLANS[A.PLANS.length - 1].id, keep: saved.keep !== false })
        else if (saved?.code) setSession({ ...(await A.verifyPasscode(saved.code, saved.name)), keep: saved.keep !== false })
      } catch { forget() }
      setLoading(false)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Just paid or started a trial and opened the app: a short welcome once the plan is loaded.
  useEffect(() => {
    if (!session) return
    let plan = null
    try { plan = sessionStorage.getItem(WELCOME_KEY); sessionStorage.removeItem(WELCOME_KEY) } catch { plan = null }
    if (plan != null) setTimeout(() => toast(`${plan || 'Your plan'} is on. Enjoy!`), 600)
  }, [session])

  // Paid, then closed the page before saving the passcode: remind them once, next time they're here.
  const [remind, setRemind] = useState(false)
  useEffect(() => {
    if (!session?.code || !A.passcodeUnsaved(session.code)) return undefined
    const t = setTimeout(() => { if (!revealsOpen && A.passcodeUnsaved(session.code)) setRemind(true) }, 1500)
    return () => clearTimeout(t)
  }, [A, session])

  useEffect(() => {
    if (!session?.expiresAt) return undefined
    const wait = session.expiresAt - Date.now()
    if (wait > 2 ** 31 - 1) return undefined
    const t = setTimeout(() => { forget(); setSession(null); toast('Your plan has ended. Renew to keep using it.') }, Math.max(0, wait))
    return () => clearTimeout(t)
  }, [session, forget])

  const persist = useCallback((next, keep = true) => {
    setSession({ ...next, keep })
    forget()
    A.writeSaved(SESSION_KEY, next.admin ? { name: next.name, admin: true, keep } : { name: next.name, code: next.code, keep }, { session: !keep })
  }, [A, SESSION_KEY, forget])

  const login = useCallback(async (code, name, keep = true) => {
    checkThrottle()
    if (!window.crypto?.subtle) throw new Error('Sign-in needs a secure (https) connection.')
    const n = String(name || '').trim(), c = String(code || '').trim()
    try {
      if (!A.looksLikePasscode(c) && await isAdmin(admin, n, c)) persist({ name: displayName(n), admin: true, plan: A.PLANS[A.PLANS.length - 1].id }, keep)
      else persist(await A.verifyPasscode(c, n), keep)
      failures.count = 0
    } catch (err) {
      if (!err.expired) noteFailure()
      if (!A.looksLikePasscode(c) && !err.expired) throw new Error('That passcode isn’t valid. It’s 12 letters and numbers, like ABCD-EFGH-JKMN.')
      throw err
    }
  }, [A, admin, persist])

  const applyPurchase = useCallback(async (p, { defer = false } = {}) => {
    const code = await A.generatePasscode(await A.purchaseDetails(p), p.name)
    A.markPasscodeUnsaved(code)
    if (p.kind === 'trial') A.markTrialUsed()
    if (!defer) persist(await A.verifyPasscode(code, p.name), session ? session.keep : true)
    return code
  }, [A, persist, session])

  const logout = useCallback(() => { forget(); setSession(null) }, [forget])
  const can = useCallback((feature) => A.hasFeature(session, feature), [A, session])
  /**
   * gate('remote', fn): runs fn when allowed; otherwise opens the upgrade sheet and returns false.
   * copy: optional sentence for the sheet instead of the feature's usual one (say, what they just used).
   */
  const gate = useCallback((feature, fn, copy) => {
    if (A.hasFeature(session, feature)) { fn?.(); return true }
    setSheet({ feature, copy })
    return false
  }, [A, session])
  const openAuth = useCallback((feature = null) => setSheet({ feature }), [])

  const value = useMemo(() => ({ A, session, loading, login, logout, applyPurchase, can, gate, openAuth, paths, gateCopy, upgradeTitle: upgradeTitle || `Unlock with ${A.product} Pro` }),
    [A, session, loading, login, logout, applyPurchase, can, gate, openAuth, paths, gateCopy, upgradeTitle])
  return (
    <AuthCtx.Provider value={value}>
      {children}
      <UpgradeSheet open={!!sheet} feature={sheet?.feature} copy={sheet?.copy} onClose={() => setSheet(null)} />
      <Sheet open={remind && !!session} onClose={() => setRemind(false)} title="Save your passcode" id="saveReminder">
        {remind && session && (
          <div className="form">
            <p className="lead">You haven’t saved the passcode for <strong>{session.name}</strong> yet. You’re signed in on this device, but you’ll need it on any other, and there’s no reset.</p>
            <PasscodeCard name={session.name} code={session.code} details={session} onSaved={() => setTimeout(() => setRemind(false), 900)} />
            <button type="button" className="btn ghost" onClick={() => { A.markPasscodeSaved(); setRemind(false) }}>I’ve already saved it</button>
          </div>
        )}
      </Sheet>
    </AuthCtx.Provider>
  )
}

/* ═══ Passcode card ═══ */
function passcodeFile(A, name, code, details) {
  const lines = [`${A.product} passcode — ${name}`, '', code, '', `Sign in with the name "${name}" and this passcode.`, 'It can’t be reset, so keep this file somewhere safe.']
  if (details) {
    lines.push('', `Plan: ${A.planById(details.plan).name}${details.trial ? ' (trial)' : ''}`)
    if (details.expiresAt) lines.push(`Valid until: ${A.formatDate(details.expiresAt)}`)
    if (details.checkoutId) lines.push(`Stripe checkout: ${details.checkoutId}`)
  }
  return lines.join('\n') + '\n'
}
function download(text, filename) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }))
  const a = Object.assign(document.createElement('a'), { href: url, download: filename })
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
const mailto = (to, subject, body) => `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`

/** onSaved: called when they copy, download or email it, so the next step can unlock. */
export function PasscodeCard({ name, code, details, onSaved }) {
  const { A } = useAuth()
  const saved = () => { A.markPasscodeSaved(); onSaved && onSaved() }
  const copy = async () => { try { await navigator.clipboard.writeText(code); toast('Passcode copied'); saved() } catch { toast('Select the passcode and copy it') } }
  const file = passcodeFile(A, name, code, details)
  return (
    <div className="passcode-card">
      <code className="passcode" aria-label="Passcode">{code}</code>
      <div className="row">
        <button type="button" className="btn sm" onClick={copy}>Copy</button>
        <button type="button" className="btn sm" onClick={() => { download(file, `${A.product.toLowerCase().split(' ')[0]}-passcode.txt`); saved() }}>Download</button>
        <a className="btn sm" href={mailto('', `${A.product} passcode — ${name}`, file)} onClick={saved}>Email it to me</a>
      </div>
    </div>
  )
}

const WELCOME_KEY = 'plan-welcome'
const firstName = (name) => displayName(name).split(' ')[0] || ''
/** What a plan unlocks, spelled out: "Everything in Creator Pass" becomes that plan's own features. */
function unlockedList(A, plan) {
  const out = []
  for (const f of plan.features) {
    const base = A.PLANS.find((p) => p.id !== plan.id && f.startsWith(`Everything in ${p.name}`))
    if (base) base.features.forEach((x) => { if (!/^Everything in/.test(x)) out.push(x) })
    else out.push(f)
  }
  return [...new Set(out)].slice(0, 4)
}

/**
 * The moment after paying, renewing or starting a trial: a short celebration, what they
 * have now, then the one thing they must do (save the passcode, since there's no reset).
 */
export function PasscodeReveal({ code, name, details, onDone, doneLabel = 'Continue' }) {
  const { A } = useAuth()
  const [saved, setSavedState] = useState(() => !A.passcodeUnsaved(code))
  const guard = useRef(!saved)
  const setSaved = (v) => {
    guard.current = !v
    if (v) A.markPasscodeSaved(); else A.markPasscodeUnsaved(code)
    setSavedState(v)
  }
  useLeaveGuard(guard)
  useEffect(() => { revealsOpen += 1; return () => { revealsOpen -= 1 } }, [])
  const plan = details?.plan ? A.planById(details.plan) : null
  const kind = details?.kind === 'renew' ? 'renew' : details?.trial ? 'trial' : 'paid'
  const who = firstName(name)
  useEffect(() => {
    // Once per passcode: a reload of this screen doesn't throw confetti again.
    const key = `celebrated:${code}`
    try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, '1') } catch { /* storage off */ }
    const t = setTimeout(() => { celebrate(); vibrate(30) }, 250)
    return () => clearTimeout(t)
  }, [code])
  const title = kind === 'renew' ? `Thanks for staying${who ? `, ${who}` : ''}!`
    : kind === 'trial' ? `Your free trial is on${who ? `, ${who}` : ''}!`
    : `You’re in${who ? `, ${who}` : ''}!`
  const sub = kind === 'renew' ? `${plan ? plan.name : 'Your plan'} is renewed. The new time is added to what you had left.`
    : kind === 'trial' ? `Everything in ${plan ? plan.name : 'Pro'} is unlocked for ${A.trialDays} days. No card, nothing to cancel.`
    : `Payment received. Welcome to ${plan ? plan.name : A.product}.`
  const finish = () => {
    guard.current = false
    try { sessionStorage.setItem(WELCOME_KEY, plan ? plan.name : '') } catch { /* storage off */ }
    onDone && onDone()
  }
  return (
    <div className="account-body win">
      <div className="win-hero">
        <svg className="win-check" viewBox="0 0 52 52" aria-hidden="true"><circle cx="26" cy="26" r="24" /><path d="M15 27l7 7 15-16" /></svg>
        <h2>{title}</h2>
        <p className="lead">{sub}{details?.test ? ' (Test mode: no payment was taken.)' : ''}</p>
        <div className="win-chips">
          {plan && <span className="win-chip">{plan.name}{kind === 'trial' ? ' trial' : ''}</span>}
          {details?.expiresAt && <span className="win-chip">Until {A.formatDate(details.expiresAt)}</span>}
          <span className="win-chip">{kind === 'trial' ? 'No card needed' : 'Doesn’t renew automatically'}</span>
        </div>
      </div>
      {plan && plan.features && plan.features.length > 0 && (
        <div className="win-unlocked">
          <span className="label">Unlocked</span>
          <ul>{unlockedList(A, plan).map((f) => <li key={f}>{f}</li>)}</ul>
        </div>
      )}
      <div className="win-step">
        <span className="label">One last step: save your passcode</span>
        <PasscodeCard name={name} code={code} details={details} onSaved={() => setSaved(true)} />
        <p className="hint">Sign in on any device with <strong>{name}</strong> and this passcode. There’s no reset, so keep it in a password manager or the file you download.</p>
        {details?.checkoutId && <p className="hint">Stripe checkout ID, for your records: <code>{details.checkoutId}</code></p>}
        <label className="check"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> I’ve saved my passcode somewhere safe</label>
      </div>
      <button type="button" className="btn primary win-go" disabled={!saved} onClick={finish}>{doneLabel}</button>
      {!saved && <p className="hint win-wait">Copy, download or email your passcode to continue.</p>}
    </div>
  )
}

/* ═══ Sign in ═══ */
const tidyCode = (v) => { const raw = v.toUpperCase().replace(/[^0-9A-Z]/g, ''); return raw.length > 6 ? raw.slice(0, 12).replace(/(.{4})(?=.)/g, '$1-') : v }

export function SignInForm({ onDone, onCancel, autoFocus = false }) {
  const { A, login, applyPurchase, paths } = useAuth()
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [show, setShow] = useState(false)
  const [keep, setKeep] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [expired, setExpired] = useState(null)
  const [renewed, setRenewed] = useState(null)
  const nameRef = useRef(null)
  useEffect(() => { if (autoFocus) nameRef.current?.focus() }, [autoFocus])

  const submit = async (e) => {
    e.preventDefault()
    setError(''); setExpired(null)
    if (!name.trim()) { setError('Enter the name on your plan.'); return }
    if (!code.trim()) { setError('Enter your passcode.'); return }
    setBusy(true)
    try {
      await login(code, name, keep)
      vibrate(); toast('Signed in')
      setTimeout(() => onDone?.(), 200)
    } catch (err) {
      setError(err.message || 'That didn’t work. Check both and try again.')
      if (err.expired) setExpired(err.expired)
      setBusy(false)
    }
  }
  const renew = async () => {
    setBusy(true); setError('')
    const pending = { kind: 'renew', plan: expired.plan, name: expired.name, code: expired.code }
    try {
      const done = await A.purchase(pending, { applyPurchase: (p) => applyPurchase(p, { defer: true }) })
      if (done) setRenewed({ code: done.code, name: pending.name, details: done.details })
    } catch (err) { setError(err.message); setBusy(false) }
  }
  if (renewed) return <PasscodeReveal {...renewed} onDone={async () => { await login(renewed.code, renewed.name, keep); onDone?.() }} />

  return (
    <form className="form" onSubmit={submit} noValidate>
      <label className="field"><span>Name</span>
        <input ref={nameRef} type="text" autoComplete="username" value={name} onChange={(e) => { setName(e.target.value); setExpired(null) }} placeholder="The name on your plan" />
      </label>
      <label className="field"><span>Passcode</span>
        <span className="pass-field">
          <input type={show ? 'text' : 'password'} autoComplete="current-password" spellCheck={false} autoCapitalize="characters"
            value={code} onChange={(e) => { setCode(tidyCode(e.target.value)); setExpired(null) }} placeholder="XXXX-XXXX-XXXX" maxLength={20} />
          <button type="button" className="btn ghost sm" onClick={() => setShow((v) => !v)} aria-pressed={show}>{show ? 'Hide' : 'Show'}</button>
        </span>
      </label>
      <label className="check"><input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} /> Keep me signed in. Turn off on a shared computer.</label>
      {error && <p className="err" role="alert">{error}</p>}
      <div className="row">
        {expired ? (
          <button type="button" className="btn primary" disabled={busy} onClick={renew}>
            {A.checkoutMode() === 'test' ? 'Renew (test mode)' : `Renew · ${A.planById(expired.plan).price}`}
          </button>
        ) : <button type="submit" className="btn primary" disabled={busy}>{busy ? 'Checking…' : 'Sign in'}</button>}
        {onCancel && <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>}
      </div>
      <details className="help">
        <summary>Lost your passcode?</summary>
        <p className="hint">Passcodes are kept only by you, so they can’t be reset. Look for the passcode file you downloaded or the email you sent yourself. Still stuck? Email <a href={`mailto:${A.SUPPORT_EMAIL}`}>{A.SUPPORT_EMAIL}</a> with your Stripe receipt.</p>
      </details>
      <p className="hint">No passcode yet? <a href={paths.start}>See plans</a></p>
    </form>
  )
}

/* ═══ Upgrade / sign-in sheet (opened by gate()) ═══ */
function UpgradeSheet({ open, feature, copy, onClose }) {
  const { A, session, applyPurchase, gateCopy, paths, upgradeTitle } = useAuth()
  const [tab, setTab] = useState('start')
  const [trialName, setTrialName] = useState('')
  const [trialDone, setTrialDone] = useState(null)
  const [err, setErr] = useState('')
  useEffect(() => { if (open) { setTab(session ? 'plans' : 'start'); setTrialDone(null); setErr('') } }, [open, session])
  const startTrial = async (e) => {
    e.preventDefault(); setErr('')
    if (!trialName.trim()) { setErr('Enter a name for your account.'); return }
    try {
      const pending = { kind: 'trial', plan: A.trialPlan, name: trialName.trim() }
      const code = await applyPurchase(pending, { defer: true })
      const v = await A.verifyPasscode(code, pending.name)
      setTrialDone({ code, name: pending.name, details: { ...pending, plan: v.plan, expiresAt: v.expiresAt, trial: true } })
    } catch (e2) { setErr(e2.message) }
  }
  const { login } = useAuth()
  return (
    <Sheet open={open} onClose={onClose} title={trialDone ? 'Welcome to Pro' : feature ? upgradeTitle : 'Sign in'} id="authSheet">
      {trialDone ? (
        <PasscodeReveal {...trialDone} doneLabel="Start using it" onDone={async () => { await login(trialDone.code, trialDone.name, true); toast('Trial started'); onClose() }} />
      ) : (
        <>
          {feature && <p className="lead gate-why"><Icon name="lock" /> {copy || gateCopy[feature] || 'This is part of the paid plan.'}</p>}
          <div className="seg" role="tablist">
            <button type="button" aria-pressed={tab === 'start'} onClick={() => setTab('start')}>{A.trialAvailable() ? 'Free trial' : 'Plans'}</button>
            <button type="button" aria-pressed={tab === 'signin'} onClick={() => setTab('signin')}>I have a passcode</button>
          </div>
          {tab === 'signin' && <SignInForm onDone={onClose} autoFocus />}
          {tab !== 'signin' && (
            <div className="form">
              {A.trialAvailable() && (
                <form onSubmit={startTrial} className="form">
                  <p className="hint">Try everything free for {A.trialDays} days. No card. You get a passcode to sign in with.</p>
                  <label className="field"><span>Name for your account</span>
                    <input value={trialName} onChange={(e) => setTrialName(e.target.value)} placeholder="Your name or channel" autoComplete="name" />
                  </label>
                  {err && <p className="err" role="alert">{err}</p>}
                  <button className="btn primary" type="submit">Start {A.trialDays}-day trial</button>
                </form>
              )}
              <div className="plan-mini">
                {A.PLANS.map((p) => (
                  <a key={p.id} className="plan-mini-row" href={`${paths.start}?plan=${p.id}`}>
                    <b>{p.name}</b><span>{p.price} <small>{p.period}</small></span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Sheet>
  )
}

/* ═══ Sign up (/start) ═══ */
function PlanPicker({ value, onChange }) {
  const { A } = useAuth()
  return (
    <fieldset className="plan-pick">
      <legend className="label">Plan</legend>
      {A.PLANS.map((p) => (
        <label key={p.id} className={`plan-opt${value === p.id ? ' on' : ''}`}>
          <input type="radio" name="plan" value={p.id} checked={value === p.id} onChange={() => onChange(p.id)} />
          <span className="plan-name">{p.name}<small>{p.blurb}</small></span>
          <span className="plan-price">{p.price}<small>{p.period}</small></span>
        </label>
      ))}
    </fieldset>
  )
}

export function SignUpView({ onOpenApp, appLabel = 'Open the app' }) {
  const { A, applyPurchase, paths } = useAuth()
  const initialPlan = new URLSearchParams(window.location.search).get('plan')
  const [plan, setPlan] = useState(A.PLANS.some((p) => p.id === initialPlan) ? initialPlan : A.PLANS[A.PLANS.length - 1].id)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(null)
  const mode = A.checkoutMode()
  const chosen = A.planById(plan)
  // Back from Stripe restores this page from the browser's cache with the button still busy.
  useEffect(() => {
    const onShow = (e) => { if (e.persisted) setBusy(false) }
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [])
  const submit = async (e) => {
    e.preventDefault(); setError('')
    if (!name.trim()) { setError('Enter a name for your account.'); return }
    setBusy(true)
    const pending = { kind: 'new', plan, name: name.trim() }
    try {
      const res = await A.purchase(pending, { applyPurchase, email })
      if (res) setDone({ code: res.code, name: pending.name, details: res.details })
    } catch (err) { setError(err.message || 'That didn’t work. Try again.'); setBusy(false) }
  }
  return (
    <div className="account-screen">
      <div className="account-card">
        {done ? <PasscodeReveal {...done} onDone={onOpenApp} doneLabel={appLabel} /> : (
          <form className="account-body" onSubmit={submit} noValidate>
            <h1>Get {A.product}</h1>
            <p className="lead">Pick a plan and pay on Stripe. Your passcode appears as soon as you’re back.</p>
            <PlanPicker value={plan} onChange={setPlan} />
            <ul className="plan-features">{chosen.features.map((f) => <li key={f}>{f}</li>)}</ul>
            <label className="field"><span>Name for your account</span>
              <input value={name} autoComplete="name" onChange={(e) => setName(e.target.value)} placeholder="Your name, channel or company" />
              <small className="hint">You’ll sign in with this exact name and your passcode.</small>
            </label>
            {mode === 'live' && (
              <label className="field"><span>Email for your receipt (optional)</span>
                <input type="email" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
                <small className="hint">Filled in for you on Stripe. It isn’t stored here.</small>
              </label>
            )}
            {error && <p className="err" role="alert">{error}</p>}
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? (mode === 'live' ? 'Opening Stripe’s secure checkout…' : 'One moment…') : mode === 'test' ? 'Create passcode (test mode, no payment)' : mode === 'off' ? `Get ${chosen.name}` : `Continue to payment · ${chosen.price} ${chosen.period}`}
            </button>
            <p className="hint">{mode === 'test'
              ? 'Test mode: the Stripe Payment Links in src/account.mjs are empty, so no payment is taken. This only happens on localhost.'
              : <>You’ll pay on Stripe’s secure page; we never see your card. Need an invoice? Email <a href={`mailto:${A.SUPPORT_EMAIL}`}>{A.SUPPORT_EMAIL}</a>.</>}</p>
            <p className="hint">Already have a passcode? <a href={paths.signin || '/signin'}>Sign in</a></p>
          </form>
        )}
      </div>
    </div>
  )
}

export function SignInView({ onDone }) {
  const { A } = useAuth()
  return (
    <div className="account-screen"><div className="account-card"><div className="account-body">
      <h1>Sign in to {A.product}</h1>
      <SignInForm onDone={onDone} autoFocus />
    </div></div></div>
  )
}

/* ═══ Back from Stripe (/checkout?session_id=cs_…) ═══ */
export function CheckoutReturnView({ onOpenApp, appLabel = 'Open the app' }) {
  const { A, applyPurchase, session } = useAuth()
  const [state, setState] = useState({ status: 'checking' })
  const ran = useRef(false)
  useEffect(() => {
    if (ran.current) return
    ran.current = true
    ;(async () => {
      const back = A.readCheckoutReturn()
      if (!back.ok) { setState({ status: 'failed', ...back }); return }
      if (back.again) { setState({ status: 'done', ...back.issued }); return }   // a reload: same passcode, not a new one
      try {
        const code = await applyPurchase(back.pending)
        const v = await A.verifyPasscode(code, back.pending.name)
        const done = { code, name: back.pending.name, details: { ...back.pending, plan: v.plan, expiresAt: v.expiresAt, checkoutId: back.sessionId } }
        A.finishCheckout(back.sessionId, done)
        setState({ status: 'done', ...done })
      } catch (err) { setState({ status: 'failed', sessionId: back.sessionId, reason: err.message }) }
    })()
  }, [A, applyPurchase])
  const support = mailto(A.SUPPORT_EMAIL, 'Passcode after payment', `Stripe checkout: ${state.sessionId || '(see my receipt email)'}\nName on the plan: \n`)
  return (
    <div className="account-screen"><div className="account-card">
      {state.status === 'done' ? <PasscodeReveal code={state.code} name={state.name} details={state.details} onDone={onOpenApp} doneLabel={appLabel} /> : (
        <div className="account-body">
          <h1>Finishing your purchase</h1>
          <p className="lead">Payment is handled by Stripe. Your passcode is made here, in your browser.</p>
          {state.status === 'checking' && <p className="hint">Checking your return from Stripe…</p>}
          {state.status === 'failed' && (
            <>
              <p className="err" role="alert">{state.reason} {state.used ? 'Sign in with the passcode you saved.' : 'If you paid, your Stripe receipt is your proof: email us with it and we’ll send your passcode. Please don’t pay again.'}</p>
              {state.sessionId && <p className="hint">Stripe checkout ID: <code>{state.sessionId}</code></p>}
              <div className="row">
                <a className="btn" href={support}>Email support</a>
                <button type="button" className="btn primary" onClick={onOpenApp}>{session ? appLabel : 'Sign in'}</button>
              </div>
            </>
          )}
        </div>
      )}
    </div></div>
  )
}

/* ═══ Plan status (menus) ═══ */
export function PlanStatus() {
  const { A, session, applyPurchase, logout, openAuth } = useAuth()
  const [busy, setBusy] = useState(false)
  const [renewed, setRenewed] = useState(null)
  const [showCode, setShowCode] = useState(false)
  if (!session) return <div className="plan-status"><span>Not signed in</span><button type="button" className="btn sm" onClick={() => openAuth()}>Sign in or try Pro</button></div>
  const plan = A.planById(session.plan)
  if (session.admin) return <div className="plan-status"><b>Admin</b><span className="hint">Every feature, no end date</span><button type="button" className="btn ghost sm" onClick={logout}>Sign out</button></div>
  const left = A.daysLeft(session.expiresAt)
  const renew = async () => {
    setBusy(true)
    const pending = { kind: 'renew', plan: session.plan, name: session.name, code: session.code }
    try { const done = await A.purchase(pending, { applyPurchase }); if (done) setRenewed({ code: done.code, name: pending.name, details: done.details }) }
    catch (err) { toast(err.message) }
    setBusy(false)
  }
  return (
    <div className="plan-status">
      <b>{session.trial ? `${plan.name} trial` : plan.name} <em className={left <= (plan.days <= 7 ? 2 : 7) ? 'warn' : ''}>{left <= 0 ? 'ends today' : `${left} day${left === 1 ? '' : 's'} left`}</em></b>
      <span className="hint">{session.name} · valid until {A.formatDate(session.expiresAt)}</span>
      <div className="row">
        <button type="button" className="btn sm" disabled={busy} onClick={renew}>{session.trial ? 'Buy a plan' : 'Renew'}{A.checkoutMode() === 'test' ? ' (test)' : ` · ${plan.price}`}</button>
        {session.code && <button type="button" className="btn ghost sm" onClick={() => setShowCode(true)}>Passcode</button>}
        {A.STRIPE.customerPortalUrl && <a className="btn ghost sm" href={A.STRIPE.customerPortalUrl} target="_blank" rel="noreferrer">Receipts</a>}
        <button type="button" className="btn ghost sm" onClick={logout}>Sign out</button>
      </div>
      <Sheet open={showCode} onClose={() => setShowCode(false)} title="Your passcode">
        {showCode && <div className="form"><PasscodeCard name={session.name} code={session.code} details={session} /><p className="hint">Sign in on any device with <strong>{session.name}</strong> and this passcode.</p></div>}
      </Sheet>
      <Sheet open={!!renewed} onClose={() => setRenewed(null)} title="New passcode">
        {renewed && <PasscodeReveal {...renewed} onDone={() => setRenewed(null)} />}
      </Sheet>
    </div>
  )
}
